import React, { useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, ExternalLinkIcon, Trash2Icon, Edit2Icon, ShieldCheckIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { ImportedBookStub, StubStatus } from './types';
interface ImportReviewQueueProps {
  stubs: ImportedBookStub[];
}
export const ImportReviewQueue: React.FC<ImportReviewQueueProps> = ({
  stubs: initialStubs
}) => {
  const dk = useDarkClasses();
  const [stubs, setStubs] = useState(initialStubs);
  const [filter, setFilter] = useState<StubStatus | 'all'>('all');
  const filteredStubs = stubs.filter(s => filter === 'all' || s.status === filter);
  const getStatusColor = (status: StubStatus) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
      case 'enriched':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'stub':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800';
    }
  };
  const getStatusLabel = (status: StubStatus) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'enriched':
        return 'Enriched';
      case 'stub':
        return 'Stub';
    }
  };
  const handleRemove = (id: string) => {
    setStubs(stubs.filter(s => s.id !== id));
  };
  if (stubs.length === 0) return null;
  return <div className="mt-12" data-id="element-18363" data-mp="37ipwf7e09ov9">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-18364" data-mp="2tlb2pqgpyuxh">
        <div data-id="element-18365" data-mp="2yk4cp8wss3sw">
          <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-18366" data-mp="2yjmc6033r3f6">
            Import Review Queue
          </h2>
          <p className={`text-sm ${dk.textSecondary}`} data-id="element-18367" data-mp="rteo27z994yh">
            Review and enrich your imported books before publishing.
          </p>
        </div>

        <div className="flex items-center gap-2" data-id="element-18368" data-mp="wqeyev350tve">
          {(['all', 'stub', 'enriched', 'verified'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${filter === f ? 'bg-purple-600 text-white border-purple-600' : `${dk.surfaceMuted} ${dk.textSecondary} ${dk.border} hover:bg-gray-100 dark:hover:bg-gray-800`}`} data-id="element-18369" data-mp="qbzmjfdnzjev">
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>)}
        </div>
      </div>

      <div className={`p-4 rounded-t-xl border border-b-0 ${dk.surfaceMuted} ${dk.border} flex justify-between items-center`} data-id="element-18370" data-mp="195cys39ll155">
        <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18371" data-mp="zdz3k7ooinht">
          {filteredStubs.length} {filteredStubs.length === 1 ? 'book' : 'books'}
        </span>
        <div className="flex gap-3" data-id="element-18372" data-mp="1s1uyb6uou5by">
          <button className={`text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300`} data-id="element-18373" data-mp="1s1mndki4la3e">
            Delete All
          </button>
          <button className="text-sm font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300" data-id="element-18374" data-mp="24bc88saw6kc6">
            Publish Verified
          </button>
        </div>
      </div>

      <div className={`border rounded-b-xl overflow-hidden ${dk.border} ${dk.card}`} data-id="element-18375" data-mp="2ebbfmlvks4ng">
        {filteredStubs.length === 0 ? <div className="p-8 text-center" data-id="element-18376" data-mp="2j7a8alnxs8to">
            <p className={dk.textSecondary} data-id="element-18377" data-mp="2na6lfwupjcq8">No books match this filter.</p>
          </div> : <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-18378" data-mp="2s1ezj66zfm9q">
            {filteredStubs.map(stub => <div key={stub.id} className="p-4 sm:p-6 flex flex-col lg:flex-row gap-6 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-18379" data-mp="2x09pqt7a4d3v">
                {/* Cover & Basic Info */}
                <div className="flex gap-4 flex-1" data-id="element-18380" data-mp="7kygmzhex7fh">
                  <div className={`w-16 h-24 shrink-0 rounded border flex items-center justify-center overflow-hidden ${dk.borderSubtle} ${dk.surfaceMuted}`} data-id="element-18381" data-mp="7jna4rwl1lko">
                    {stub.coverUrl ? <img src={stub.coverUrl} alt={stub.title} className="w-full h-full object-cover" data-id="element-18382" data-mp="hckmshew8gbd" /> : <span className={`text-xs text-center px-1 ${dk.textMuted}`} data-id="element-18383" data-mp="3fswzkkdj9nz3">
                        No Cover
                      </span>}
                  </div>

                  <div className="flex flex-col" data-id="element-18384" data-mp="3pp01bp4h3kli">
                    <div className="flex items-center gap-2 mb-1" data-id="element-18385" data-mp="191nte3u962r6">
                      <h3 className={`font-bold ${dk.textPrimary}`} data-id="element-18386" data-mp="1940jx5e2tlfu">
                        {stub.title}
                      </h3>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium ${getStatusColor(stub.status)}`} data-id="element-18387" data-mp="1ck4qeuylbgfq">
                        {getStatusLabel(stub.status)}
                      </span>
                    </div>
                    <p className={`text-sm mb-2 ${dk.textSecondary}`} data-id="element-18388" data-mp="17k8nz05jjx2a">
                      {stub.authorName}
                    </p>

                    <div className="flex flex-wrap gap-2 mt-auto" data-id="element-18389" data-mp="1vauw0rfjphca">
                      <span className={`text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300`} data-id="element-18390" data-mp="209pc3lm4orsp">
                        {stub.format}
                      </span>
                      {stub.isbn13 && <span className={`text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300`} data-id="element-18391" data-mp="2e9gtlwbusqbg">
                          ISBN: {stub.isbn13}
                        </span>}
                      {stub.asin && <span className={`text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300`} data-id="element-18392" data-mp="2nxw09u7useze">
                          ASIN: {stub.asin}
                        </span>}
                    </div>
                  </div>
                </div>

                {/* Status & Buy Links */}
                <div className="flex flex-col gap-4 lg:w-1/3" data-id="element-18393" data-mp="255a75hobz2ii">
                  <div data-id="element-18394" data-mp="2078dk2ta3kss">
                    <div className="flex justify-between text-xs mb-1" data-id="element-18395" data-mp="206gshlxb55gr">
                      <span className={dk.textSecondary} data-id="element-18396" data-mp="1w5n64wzijypz">
                        Profile Completeness
                      </span>
                      <span className={`font-medium ${dk.textPrimary}`} data-id="element-18397" data-mp="1r6ts93gbe5pa">
                        {stub.completionPercent}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-18398" data-mp="1pony29dqf0b">
                      <div className={`h-full rounded-full ${stub.completionPercent === 100 ? 'bg-green-500' : 'bg-purple-500'}`} style={{
                  width: `${stub.completionPercent}%`
                }} data-id="element-18399" data-mp="6nzy6e3l6rj2" />
                    </div>
                    {stub.missingFields.length > 0 && <p className={`text-xs mt-1.5 ${dk.textMuted}`} data-id="element-18400" data-mp="3k362ph0izg4a">
                        Missing: {stub.missingFields.slice(0, 3).join(', ')}
                        {stub.missingFields.length > 3 && ` +${stub.missingFields.length - 3} more`}
                      </p>}
                  </div>

                  {stub.buyLinks.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-18401" data-mp="30ff1bo2vkw5b">
                      {stub.buyLinks.map((link, idx) => <a key={idx} href={link.url} target="_blank" rel="noreferrer" className={`inline-flex items-center text-[10px] px-2 py-1 rounded border ${dk.borderSubtle} ${dk.textSecondary} hover:${dk.textPrimary} transition-colors`} data-id="element-18402" data-mp="1trkwsc0vet0u" data-mpi={idx}>
                          {link.platform}{' '}
                          <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-18403" data-mp="2cp5mtmfdfxn1" />
                        </a>)}
                    </div>}
                </div>

                {/* Actions */}
                <div className="flex lg:flex-col justify-end gap-2 shrink-0" data-id="element-18404" data-mp="2mimh0rjf7er8">
                  {stub.status !== 'verified' && <button className="flex items-center justify-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-18405" data-mp="2vgombja6stkb">
                      <ShieldCheckIcon className="w-4 h-4 mr-2" data-id="element-18406" data-mp="3a4euxk1w9j5s" />
                      Claim & Verify
                    </button>}
                  <button className={`flex items-center justify-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${dk.border} ${dk.textPrimary} hover:bg-gray-50 dark:hover:bg-gray-800`} data-id="element-18407" data-mp="3f2q5xy3xdzpl">
                    <Edit2Icon className="w-4 h-4 mr-2" data-id="element-18408" data-mp="2rdxg8jdwco3f" />
                    Edit Details
                  </button>
                  <button onClick={() => handleRemove(stub.id)} className={`flex items-center justify-center p-2 border rounded-lg transition-colors ${dk.border} text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20`} aria-label="Remove" data-id="element-18409" data-mp="2w4nllwmboqy4">
                    <Trash2Icon className="w-4 h-4" data-id="element-18410" data-mp="28g4evlix8m85" />
                  </button>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
};